import { Button, IconButton, Row, ShimmerText, Tooltip } from "@narmi/design_system";
import { SavedReply } from "@pages/configuration/messages/types";
import { useEffect, useState } from "react";
import { useThread } from "../hooks/useThread";
import { AiReplyBoxTextInput } from "./AiReplyBoxTextInput";
import { SavedReplyPreview } from "./SavedReplyPreview";
import styles from "./style.module.css";

export const ReplyBoxContents = ({
  isInternalNoteMode,
  savedReplyPreview,
  setSavedReplyPreview,
  onTextChange,
  onSavedReplyAccept,
  textareaRef,
  textareaHeight,
}: {
  isInternalNoteMode: boolean;
  savedReplyPreview: SavedReply | null;
  setSavedReplyPreview: React.Dispatch<React.SetStateAction<SavedReply | null>>;
  onTextChange: (value: string) => void;
  onSavedReplyAccept: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  textareaHeight: number | null;
}) => {
  const [prompt, setPrompt] = useState("");

  const {
    handleGenerateReply,
    isGeneratingReply,
    setAiDraftDismissed,
    reply,
    isRegenerating,
    aiDraft,
    setIsRegenerating,
    aiDraftAvailable,
    isSubmitting,
    thread,
  } = useThread();

  const displayDraft = aiDraftAvailable && !reply.value.trim();

  const backgroundColor = displayDraft
    ? "#5514b80d"
    : isInternalNoteMode
      ? "var(--color-warnLight)"
      : "var(--bgColor-white)";

  useEffect(() => {
    // A manually dragged height takes precedence over auto-sizing to content.
    if (textareaRef.current && textareaHeight == null) {
      // Adjust the height of the textarea to fit the content
      textareaRef.current.style.height = "";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [reply, aiDraft?.body, aiDraftAvailable, textareaHeight, textareaRef]);

  return (
    <div className={`${!isRegenerating ? "margin--bottom--s" : ""}`}>
      <div
        className={styles.replyBodyContainer}
        style={{
          backgroundColor,
          border: displayDraft
            ? "transparent"
            : "var(--border-size-s) solid var(--color-lightGrey)",
        }}
      >
        {aiDraft?.status === "pending" ? (
          <div className="padding--all--s">
            <ShimmerText text="Thinking..." />
          </div>
        ) : (
          <>
            {displayDraft && aiDraft?.status === "ready" && !isRegenerating && (
              <div className={styles.aiDraftDismissButton}>
                <Tooltip text="Dismiss draft">
                  <IconButton
                    kind="action"
                    name="x"
                    disabled={isSubmitting}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setAiDraftDismissed(true);
                    }}
                    textSize="m"
                  />
                </Tooltip>
              </div>
            )}
            <textarea
              ref={textareaRef}
              placeholder={`Reply to ${thread.user.full_name}`}
              value={displayDraft ? aiDraft?.body : reply.value}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onTextChange(e.target.value)}
              className={styles.replyInput}
              disabled={isSubmitting}
              style={{
                backgroundColor: "transparent",
                ...(textareaHeight != null
                  ? { height: `${textareaHeight}px`, maxHeight: `${textareaHeight}px` }
                  : { maxHeight: isRegenerating ? "250px" : "150px" }),
              }}
            />
            {savedReplyPreview && (
              <SavedReplyPreview
                savedReply={savedReplyPreview}
                onAccept={onSavedReplyAccept}
                onDismiss={() => setSavedReplyPreview(null)}
                disabled={isSubmitting}
              />
            )}
          </>
        )}
        {isRegenerating && (
          <div className="padding--all--s">
            <Row alignItems="center" gapSize="xxs">
              <Row.Item>
                <AiReplyBoxTextInput
                  placeholder="Provide additional instructions to Narmi AI"
                  required={false}
                  value={prompt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrompt(e.target.value)}
                  isLoading={isGeneratingReply}
                  disabled={isGeneratingReply || isSubmitting}
                  endContent={
                    prompt !== "" && (
                      <div className="margin--right--xxs">
                        <Button
                          size="s"
                          kind="plain"
                          label="Regenerate"
                          onClick={() => {
                            handleGenerateReply("regenerate", prompt);
                            setPrompt("");
                          }}
                          disabled={isGeneratingReply || isSubmitting}
                        />
                      </div>
                    )
                  }
                />
              </Row.Item>
              <Row.Item shrink>
                <Tooltip text="Cancel regeneration">
                  <IconButton
                    kind="action"
                    name="x"
                    onClick={() => setIsRegenerating(false)}
                    disabled={isSubmitting}
                  />
                </Tooltip>
              </Row.Item>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};
